import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ROUTES from "../../utils/ROUTES";
import assetPath from "../../utils/assetPath";
import Modal from "./Modal";
import ChangePasswordForm, { ChangePasswordFormValues } from "../auth/ChangePasswordForm";
import { toast } from "../../hooks/use-toast";
import { useChangePassword } from "../../services/authentication";
import axios from "axios";

const imgLogo           = assetPath("/logo.png");
const imgIconDashboard  = assetPath("/icons/dashboard-icon.png");
const imgIconRewards    = assetPath("/icons/rewards-icon.png");
const imgIconUsers      = assetPath("/icons/users-icon.png");
const imgIconLogout     = assetPath("/icons/logout-icon.png");

const navItems = [
  { label: "Dashboard",          route: ROUTES.DASHBOARD, icon: imgIconDashboard },
  { label: "Loyalty",            route: ROUTES.LOYALTY,   icon: imgIconDashboard },
  { label: "Rewards",            route: ROUTES.REWARDS,   icon: imgIconRewards   },
  { label: "Offers",             route: ROUTES.OFFERS,    icon: imgIconDashboard },
  { label: "Campaigns",          route: ROUTES.CAMPAIGNS, icon: imgIconDashboard },
  { label: "Dietary Preference", route: ROUTES.DIETARY,   icon: imgIconDashboard },
  { label: "Staff",              route: ROUTES.USERS,     icon: imgIconUsers     },
];

function getInitials(displayName?: string | null, email?: string | null): string {
  if (displayName) {
    const parts = displayName.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email ? email.slice(0, 2).toUpperCase() : "??";
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isOpen = false, onClose }: SidebarProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initials = getInitials(user?.displayName, user?.email);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { mutate: changePassword, isPending: isChangingPassword } = useChangePassword();

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const handleNav = (route: string) => {
    navigate(route);
    onClose?.();
  };

  const handleChangePassword = (values: ChangePasswordFormValues) => {
    changePassword(values, {
      onSuccess: () => { toast({ variant: "success", title: "Password changed", description: "Your password has been successfully updated." }); setChangePasswordOpen(false); },
      onError: (err) => { toast({ variant: "error", title: "Failed to change password", description: axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again." }); },
    });
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-30 flex flex-col w-[248px] shrink-0 h-screen
          rounded-br-[20px] rounded-tr-[20px] overflow-hidden
          transition-transform duration-200 ease-in-out
          lg:static lg:translate-x-0 bg-brand-primary
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo */}
        <div className="flex items-center px-[41px] py-3 h-[80px]">
          <img src={imgLogo} alt="The Coconut Tree" className="h-[56px] w-[166px] object-cover" />
        </div>

        {/* Nav links */}
        <nav className="flex flex-col gap-5 mt-[9px] flex-1">
          {navItems.map((item) => {
            const isActive = pathname.startsWith(item.route);
            return (
              <button
                key={item.label}
                onClick={() => handleNav(item.route)}
                className={`flex items-center gap-3 px-4 py-3 rounded-[6px] w-full text-left transition-colors ${
                  isActive ? "bg-white/15" : "hover:bg-white/10"
                }`}
              >
                <img src={item.icon} alt="" className="w-[18px] h-[18px] shrink-0" />
                <span className="text-[16px] font-medium leading-none text-surface-page">
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Bottom: logout + user info */}
        <div className="flex flex-col gap-[10px]">
          <button
            onClick={() => setChangePasswordOpen(true)}
            className="flex items-center gap-3 px-4 py-2 rounded-[6px] w-full text-left hover:bg-white/10 transition-colors"
          >
            <KeyRound className="w-[15px] h-[15px] shrink-0 text-surface-page" />
            <span className="text-[16px] font-medium leading-none text-surface-page">
              Change Password
            </span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2 rounded-[6px] w-full text-left hover:bg-white/10 transition-colors"
          >
            <img src={imgIconLogout} alt="" className="w-[15px] h-[15px] shrink-0" />
            <span className="text-[16px] font-medium leading-none text-surface-page">
              Logout
            </span>
          </button>

          <div className="flex items-center gap-3 px-[10px] pt-[10px] pb-5">
            <div
              className="flex items-center justify-center rounded-[12px] w-8 h-8 shrink-0 text-[12px] font-medium text-surface-card"
              style={{ backgroundColor: "#6ca5a1" }}
            >
              {initials}
            </div>
            <span className="text-[12px] font-medium truncate text-surface-page">
              {user?.email ?? ""}
            </span>
          </div>
        </div>
      </aside>

      <Modal isOpen={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} title="Change Password">
        <ChangePasswordForm
          onSave={handleChangePassword}
          onCancel={() => setChangePasswordOpen(false)}
          isPending={isChangingPassword}
        />
      </Modal>
    </>
  );
};

export default Sidebar;
